import { AODA } from "@core/utilities/aoda";
import {
  Component,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { BaseComponent } from "@shared/components/base.component";
import { FormGroup } from "@angular/forms";
import { startWith, takeWhile, filter, switchMap, exhaustMap, tap } from "rxjs/operators";
import { ElasticsearchQueryService } from "@shared/services/elasticsearch-query.service";
import { Utils } from "@core/utilities/utils";
import { Observable, of, Subject } from "rxjs";

@Component({
  selector: "app-township-filter",
  templateUrl: "./township-filter.component.html",
  styleUrls: ["./township-filter.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class TownshipFilter extends BaseComponent implements OnInit, OnDestroy {

  @Input() f: FormGroup;
  @Input() clearFilter: boolean;
  @Input() enableFilter: boolean;
  displayTownshipFilter: boolean = false;
  public height: string;
  totalFilterValues: number = 0;

  //Auto-Complete Variables
  lastViewedPage: number;
  lastFilter = "";
  townships: any[] = [];
  selectedTownships: any[] = new Array<any>();
  filteredTownships: any[];
  isAllSelected = false;
  isInitialized = false;
  filteredTownships$: Observable<any[]> | undefined;
  private nextPage$ = new Subject();

  constructor(
    private injector: Injector,
    private el: ElementRef,
    private r2: Renderer2,
    private searchFiltersService: ElasticsearchQueryService
  ) {
    super(injector);
  }

  ngOnInit() {
    super.ngOnInit();

    if(this.f.get("township").value.length > 0) {
      this.displayTownshipFilter = true;
      this.makeTownshipRequest();
    } else {
      this.displayTownshipFilter = false;
    }

    this.selectedTownships = this.f.get("township").value;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    AODA.applyAllFixes(this.el, this.r2, this.languageService.languageType);
  }

  ngOnChanges() {
    //Reset the last viewed page if click outside Township filter area
    this.r2.listen('window', 'click',(e:Event)=>{
      this.lastViewedPage = 1;
      this.tempTownship.setValue("");
      this.getObservableValues();
    });

    if(this.clearFilter) {
      this.unselectAll();
      this.clearFilter = false;
    }

    if(!this.enableFilter) {
      this.displayTownshipFilter = false;
    }
  }

  getTownshipList(startsWith: any, page: number): Observable<any[]> {
    const take = 10;
    const skip = page > 0 ? (page - 1) * take : 0;
    const filtered = this.townships.filter((option) =>
      option._source.name.toLowerCase().startsWith(startsWith.toLowerCase())
    );
    this.lastViewedPage = page;
    return of(filtered.slice(0, skip + take));
  }

  displayWith(element: any) {
    return element ? element.name : null;
  }

  onScroll() {
    this.nextPage$.next(null);
  }

  //Filter Functions
  onDisplayTownshipFilter() {
    if(this.enableFilter) {
      if(!this.displayTownshipFilter && !this.isInitialized)  {
        this.makeTownshipRequest();
      }
      this.displayTownshipFilter = !this.displayTownshipFilter;
    }
  }

  makeTownshipRequest() {
    this.isInitialized = true;
    this.searchFiltersService.getTownship().pipe(
      takeWhile(() => this.alive),
    ).subscribe((townships) => {
      this.totalFilterValues = townships.hits.hits.length;
      this.townships = this.filteredTownships = Utils.sortElasticSearchFilterList(townships.hits.hits, 'name');
      this.getObservableValues();
    });
  }

  getObservableValues() {
    const filter$ = this.tempTownship.valueChanges.pipe(
      startWith(""),
      filter((q) => typeof q === "string")
    );

    this.filteredTownships$ = filter$.pipe(
      switchMap((filter) => {
        let currentPage = this.lastViewedPage;
        return this.nextPage$.pipe(
          startWith(currentPage),
          exhaustMap((_) => this.getTownshipList(filter, currentPage)),
          tap(() => currentPage++)
        );
      })
    );
  }
  //Filter Functions

  isTownshipChecked(item: any) {
    if(this.isSelected(item)) {
      item.selected = true;
      return true
    }
    item.selected = false;
    return false;
  }

  isSelected(item: any): boolean {
    return this.township.value.includes(item._source.name)
  }

  updateAllSelected() {
    return this.isAllSelected = this.township.value.length == this.totalFilterValues ? true : false
  }

  //Auto-Complete
  toggleSelectAll(event: any) {
    //Needed for mat-option and mat-checkbox
    if (event.isUserInput || event.source._currentCheckState) {
      this.isAllSelected = !this.isAllSelected;
      this.selectedTownships = [];

      for (let i = 0; i < this.townships.length; i++) {
        this.townships[i].selected = this.isAllSelected;

        if (this.isAllSelected) {
          this.selectedTownships.push(this.townships[i]._source.name);
        }
      }
      this.updateTownships(this.selectedTownships);
    }
  }

  unselectAll() {
    for (let i = 0; i < this.townships.length; i++) {
      this.townships[i].selected = false;
    }
    this.selectedTownships = [];
    this.updateTownships(this.selectedTownships);
  }

  toggleSelection(item: any, event: any) {
    //Needed for mat-option and mat-checkbox
    if (event.isUserInput || event.source._currentCheckState) {
      item.selected = !item.selected;
      if (item.selected && !this.isSelected(item)) {
        this.selectedTownships.push(item._source.name);
      } else {
        const i = this.selectedTownships.findIndex(
          (value) => value === item._source.name
        );
        this.selectedTownships.splice(i, 1);
      }
      this.updateTownships(this.selectedTownships);
    }
  }

  updateTownships(townshipList: any[]) {
    var townships = [];
    for (var i = 0; i < townshipList.length; i++) {
      townships.push(townshipList[i]);
    }
    this.township.setValue(townships);
  }
  //Auto-Complete

  get township() {
    return this.f.get("township");
  }

  get tempTownship() {
    return this.f.get("tempTownship");
  }
}
